/**
 * How people move in a building (view only).
 *
 * The sim says where someone is — waiting on a floor, riding, delivered,
 * gone — and the moment it changed. This turns those changes into movement a
 * person would make: walking in from the corridor instead of appearing,
 * stepping up the queue instead of jumping, stepping into the car, getting out
 * at their floor and walking off, turning away when they give up. Legs swing
 * as they walk. While they wait they breathe and blink, look round now and
 * then and turn to the lift when it is coming for them, and the impatient tap
 * a foot faster as their patience runs out.
 *
 * Nothing here changes a rule or a timing: it only reads. A person who is
 * still walking in is already waiting in the sim, so the queue the player sees
 * settles at once and the motion only ever lags behind the truth by a step.
 * Small on purpose, too: the ticket must stay readable, so a waiting ticket
 * never moves more than a pixel or two.
 */

/** Walking pace along a floor, px per second. */
export const WALK_SPEED = 300;

/** How long each way of leaving the floor takes, in ms. */
export const EXIT_MS = {
  /** Into the car: the sim moves one rider per door slot (220 ms on the stock doors). */
  board: 280,
  /** Out of the car at their floor, and off into the room. */
  alight: 900,
  /** Out of patience: turning away and going. */
  storm: 800,
} as const;

export type Exit = keyof typeof EXIT_MS;

/** A stable number per person, so no two people breathe or blink in step. */
export function seedOf(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0xffffffff;
}

/** Move toward a target by at most `step`, never past it. */
export function approach(current: number, target: number, step: number): number {
  const gap = target - current;
  if (Math.abs(gap) <= step) return target;
  return current + Math.sign(gap) * step;
}

/** The little lift of each step while walking: 0 to -2 px. */
export function walkBob(ms: number): number {
  return -Math.abs(Math.sin((ms / 260) * Math.PI)) * 2;
}

/** How far each leg swings either side of straight while walking, in radians. */
export const STRIDE = (16 * Math.PI) / 180;

/**
 * One leg's swing while walking, in radians; the other leg is its opposite.
 * In step with `walkBob`: legs furthest apart as the body is lowest, passing
 * each other as it rises.
 */
export function legSwing(ms: number): number {
  return STRIDE * Math.cos((ms / 260) * Math.PI);
}

/** How fast an impatient foot taps, in ms per tap: quicker as patience runs out. */
function tapPeriod(urgency: number): number {
  return 520 - Math.min(1, urgency) * 260;
}

/**
 * An impatient foot, 0 (down) to 1 (lifted): quicker as patience runs out,
 * never while calm. Where feet are drawn the foot taps and the ticket keeps
 * still; where they are not, `idleOffset` taps the whole person instead.
 */
export function toeLift(ms: number, seed: number, urgency: number): number {
  if (urgency <= 0) return 0;
  return Math.abs(Math.sin(((ms + seed * 1000) / tapPeriod(urgency)) * Math.PI));
}

/** How far eyes turn to one side, in head units: inside the glasses' rims. */
export const GAZE_UNITS = 1.3;

/**
 * Where a waiting person is looking: -1 toward the lift, 1 away from it, 0
 * straight out. Toward the lift whenever it is coming for their floor;
 * otherwise mostly ahead, with a look round every several seconds, each
 * person on their own rhythm and to their own side.
 */
export function gaze(ms: number, seed: number, liftComing: boolean): number {
  if (liftComing) return -1;
  const period = 6000 + seed * 5000;
  const at = (ms + seed * period) % period;
  if (at >= 1100) return 0;
  return Math.floor((ms + seed * period) / period) % 2 === 0 ? 1 : -1;
}

/**
 * Standing and waiting, as a vertical offset in px.
 *
 * Calm, a slow breath of under a pixel. Impatient (`urgency` 0..1, how much
 * of their patience is gone past half), a tapping foot that quickens as it
 * runs out — never more than 1.5 px, so the ticket stays readable. Pass 0 for
 * someone whose feet are drawn: their foot taps instead (`toeLift`).
 */
export function idleOffset(ms: number, seed: number, urgency: number): number {
  const breath = Math.sin(((ms + seed * 4000) / (2600 + seed * 800)) * Math.PI * 2) * 0.7;
  if (urgency <= 0) return breath;
  return -toeLift(ms, seed, urgency) * 1.5;
}

/** Eyes shut for a moment every few seconds, each person on their own rhythm. */
export function blinking(ms: number, seed: number): boolean {
  const period = 3200 + seed * 2400;
  return (ms + seed * period) % period < 130;
}

export interface Pose {
  readonly x: number;
  readonly dy: number;
  readonly alpha: number;
  readonly scale: number;
  /** Striding, legs swinging, rather than hopping down from the car. */
  readonly walking: boolean;
}

/**
 * Where someone leaving the floor is, `t` (0..1) of the way through it.
 *
 * Boarding walks from their place into the car and shrinks into it; alighting
 * hops out of the car and walks into the room, fading; giving up walks away
 * from the lift, fading.
 */
export function exitPose(kind: Exit, t: number, from: number, to: number): Pose {
  const p = Math.min(1, Math.max(0, t));
  const x = from + (to - from) * p;
  switch (kind) {
    case 'board':
      return { x, dy: 0, alpha: 1 - p * p, scale: 1 - 0.3 * p, walking: true };
    case 'alight': {
      const hopping = p < 0.25;
      const dy = hopping ? -Math.sin((p / 0.25) * Math.PI) * 5 : walkBob(p * EXIT_MS.alight);
      const alpha = p < 0.4 ? 1 : 1 - (p - 0.4) / 0.6;
      return { x, dy, alpha, scale: 1, walking: !hopping };
    }
    case 'storm':
      return { x, dy: walkBob(p * EXIT_MS.storm * 1.4), alpha: 1 - p, scale: 1, walking: true };
  }
}
